import type { IconPlaceholder } from "@tabler/icons-vue";
import { nanoid } from "nanoid";

export type PromptState = {
  prompts: Prompt[];
};

export type PromptButton = {
  icon?: typeof IconPlaceholder;
  action: "primary" | "cancel" | "danger";
  text: string;
};

export type PromptResolutionHook = (
  prompt: Prompt,
  action: PromptButton["action"],
) => void;

export type PromptIcon =
  | "angry"
  | "confused"
  | "cry"
  | "empty"
  | "happy"
  | "laugh"
  | "love"
  | "no_connection"
  | "no_search_results"
  | "okay"
  | "sad"
  | "sleep"
  | "surprised"
  | "thankyou"
  | "wink";

export class Prompt {
  public buttons: PromptButton[] = [];
  public description?: string;
  public icon?: PromptIcon;

  constructor(
    public title: string,
    public id = nanoid(),
  ) {}

  setDescription(description: string) {
    this.description = description;
    return this;
  }

  addButton(buttonDef: PromptButton) {
    this.buttons.push(buttonDef);
    return this;
  }

  primaryButton(text: string, icon?: PromptButton["icon"]) {
    return this.addButton({
      action: "primary",
      icon,
      text,
    });
  }

  cancelButton(text: string = "Cancel") {
    return this.addButton({
      action: "cancel",
      text,
    });
  }

  dangerButton(text: string, icon?: PromptButton["icon"]) {
    return this.addButton({
      action: "danger",
      text,
      icon,
    });
  }

  twoButtons(
    defs: { cancel: string } & ({ primary: string } | { danger: string }),
  ) {
    this.buttons.splice(0, this.buttons.length);

    this.cancelButton(defs.cancel);
    if ("primary" in defs) this.primaryButton(defs.primary);
    else this.dangerButton(defs.danger);
    return this;
  }

  threeButtons(defs: { cancel: string; primary: string; danger: string }) {
    this.buttons.splice(0, this.buttons.length);

    this.cancelButton(defs.cancel);
    this.primaryButton(defs.primary);
    this.dangerButton(defs.danger);
    return this;
  }

  setIcon(icon: PromptIcon) {
    this.icon = icon;
    return this;
  }
}

export function usePromptState() {
  return useState<PromptState>("prompts", () => ({
    prompts: [],
  }));
}

export function usePromptResolver() {
  const resolutionHooks = useState<Record<string, PromptResolutionHook[]>>(
    "prompt-resolution-hooks",
    () => ({}),
  );
  const state = usePromptState();

  function resolve(id: string, action: PromptButton["action"]) {
    const prompt = state.value.prompts.find((p) => p.id === id);
    if (!prompt) {
      console.warn(
        `Could not execute hooks for prompt ${id} because it was not found in the state.`,
      );
      return;
    }

    resolutionHooks.value[id]?.forEach((hook) => hook(prompt, action));
  }

  function onResolution(id: string, hook: PromptResolutionHook) {
    if (!(id in resolutionHooks)) resolutionHooks.value[id] = [hook];
    else resolutionHooks.value[id].push(hook);
  }

  return {
    resolve,
    onResolution,
  };
}

export function usePrompt() {
  const state = usePromptState();
  const { onResolution } = usePromptResolver();

  async function getAction(prompt: Prompt) {
    state.value.prompts.push(prompt);

    return new Promise<PromptButton["action"]>((resolve, reject) => {
      onResolution(prompt.id, (_prompt, action) => {
        resolve(action);
      });
    });
  }

  async function assertAction(prompt: Prompt, action: PromptButton["action"]) {
    const actualACtion = await getAction(prompt);
    if (actualACtion !== action)
      throw new Error(
        `Expected prompt action to be ${action} but was instead ${actualACtion}`,
      );
  }

  return {
    assertAction,
    getAction,
  };
}
